// src/components/Scorecard/Scorecard.js
import React, { useState, useEffect, useMemo } from "react";
import { Container, Typography, Snackbar, Alert } from "@mui/material";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase-config";
import { useAuth } from "../../context/AuthContext";
import CourseSelector from "./CourseSelector";
import ScorecardOptions from "./ScorecardOptions";
import ScorecardTable from "./ScorecardTable";
import ScorecardFooter from "./ScorecardFooter";
// Import the available helper functions.
import { getCompetitionWindowStartForDate, getCompetitionDeadlineForWindowStart } from "../../utils/competitionWindow";
// Import fromZonedTime (or from the appropriate function) to convert Eastern time to UTC.
import { fromZonedTime } from "date-fns-tz";

const Scorecard = () => {
  const { user } = useAuth();

  const [scores, setScores] = useState(Array(18).fill(""));
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [holeData, setHoleData] = useState(
    Array(18).fill({ par: "", yardage: "", handicap: "" })
  );
  const [roundDate, setRoundDate] = useState("");
  const [isCompetition, setIsCompetition] = useState(false);
  const [numHoles, setNumHoles] = useState("18");
  const [nineChoice, setNineChoice] = useState("Front");
  const [selectedTee, setSelectedTee] = useState(null);
  // New state to force re-mounting of CourseSelector for a fresh form.
  const [formKey, setFormKey] = useState(Date.now());

  // Snackbar state for feedback.
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handleCourseSelect = (courseObj, date, defaultHoleData) => {
    setSelectedCourse(courseObj);
    setRoundDate(date);
    setHoleData(defaultHoleData);
  };

  const handleScoreChange = (index, value) => {
    const numericValue = value.replace(/\D/g, "");
    const updated = [...scores];
    updated[index] = numericValue;
    setScores(updated);
  };

  const outScore = scores
    .slice(0, 9)
    .reduce((sum, s) => sum + (parseInt(s, 10) || 0), 0);
  const inScore = scores
    .slice(9)
    .reduce((sum, s) => sum + (parseInt(s, 10) || 0), 0);
  const totalScore = outScore + inScore;

  const teeOptions = useMemo(() => {
    if (!selectedCourse || !selectedCourse.tees) return [];
    let options = [];
    if (user && user.gender) {
      const gender = user.gender.toLowerCase();
      options =
        gender === "male"
          ? selectedCourse.tees.male || []
          : selectedCourse.tees.female || [];
    } else {
      options = [
        ...(selectedCourse.tees.male || []),
        ...(selectedCourse.tees.female || []),
      ];
    }
    return Array.from(
      new Map(options.map((tee) => [tee.tee_name, tee])).values()
    );
  }, [selectedCourse, user]);

  useEffect(() => {
    if (teeOptions.length > 0 && !selectedTee) {
      setSelectedTee(teeOptions[0]);
    }
  }, [teeOptions, selectedTee]);

  // Compute competition window based on current date.
  const competitionWindowStart = getCompetitionWindowStartForDate(new Date());
  const competitionDeadline = getCompetitionDeadlineForWindowStart(
    competitionWindowStart
  );

  const holeOffset =
    numHoles === "9" && nineChoice === "Back" ? 9 : 0;

  const scorecardComplete = (() => {
    if (!selectedCourse) return false;
    if (numHoles === "18") {
      return scores.slice(0, 18).every((score) => score.trim() !== "");
    } else if (numHoles === "9") {
      return nineChoice === "Front"
        ? scores.slice(0, 9).every((score) => score.trim() !== "")
        : scores.slice(9, 18).every((score) => score.trim() !== "");
    }
    return false;
  })();

  // Reset the form state including clearing the selected course.
  const resetScorecardForm = () => {
    setScores(Array(18).fill(""));
    setSelectedCourse(null);
    setHoleData(Array(18).fill({ par: "", yardage: "", handicap: "" }));
    setRoundDate("");
    setIsCompetition(false);
    setNumHoles("18");
    setNineChoice("Front");
    setSelectedTee(null);
    // Generate a new key so that CourseSelector remounts
    setFormKey(Date.now());
  };

  const finalSubmit = async () => {
    if (!user) {
      setSnackbarMessage("You must be logged in to save scores!");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    const rawScore = totalScore;
    const courseRating = selectedTee ? selectedTee.course_rating : 0;
    const slopeRating = selectedTee ? selectedTee.slope_rating : 113;
    const playerHandicap = user.handicap;
    const differential =
      ((rawScore - playerHandicap - courseRating) * 113) / slopeRating;

    try {
      // Convert the entered roundDate (a string like "2025-05-05")
      // into a full timestamp. Append "T00:00:00" if necessary.
      const dateString = roundDate.includes("T") ? roundDate : `${roundDate}T00:00:00`;
      // Convert from Eastern Time to UTC.
      const utcTimestamp = fromZonedTime(dateString, "America/New_York");

      const scorecardData = {
        courseName: selectedCourse
          ? `${selectedCourse.courseName} - ${selectedCourse.clubName}`
          : "",
        clubName: selectedCourse ? selectedCourse.clubName : "Unknown Club",
        roundDate: utcTimestamp, // Stored as a full UTC timestamp.
        isCompetition,
        numHoles: parseInt(numHoles, 10),
        nineChoice: numHoles === "9" ? nineChoice : null,
        selectedTee: selectedTee ? selectedTee.tee_name : null,
        teeData: teeOptions.reduce((acc, tee) => {
          if (
            selectedTee &&
            selectedTee.tee_name.toLowerCase() ===
              tee.tee_name.toLowerCase()
          ) {
            const yardages = tee.holes
              .map((h) => parseInt(h.yardage, 10) || "")
              .slice(0, 18);
            const total = yardages.reduce(
              (sum, val) => sum + (parseInt(val, 10) || 0),
              0
            );
            acc[tee.tee_name] = { yardages, total };
          }
          return acc;
        }, {}),
        scores: scores.map((score, index) => ({
          hole: index + 1,
          score: parseInt(score, 10) || 0,
          par: selectedTee ? parseInt(selectedTee.holes[index].par, 10) || 0 : 0,
          handicap: selectedTee ? selectedTee.holes[index].handicap || "" : "",
        })),
        aggregates: {
          yardage: {},
          par: {},
          score: { out: outScore, in: inScore, total: totalScore },
        },
        differential,
        course_rating: courseRating,
        slope_rating: slopeRating,
        submittedAt: serverTimestamp(),
      };

      await setDoc(
        doc(db, "users", user.uid, "scorecards", new Date().toISOString()),
        scorecardData
      );
      setSnackbarMessage("Scorecard saved successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      // Reset the form after successful submission.
      resetScorecardForm();
    } catch (error) {
      console.error("Error saving scorecard:", error);
      setSnackbarMessage("Failed to save scorecard.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Enter New Round
      </Typography>
      <Typography variant="body1" gutterBottom>
        Played a new round lately? Enter your score here.
        Don't forget to toggle "Post For Competition Play" for weekly regional/team Low Round entry!
      </Typography>
      <CourseSelector key={formKey} onCourseSelect={handleCourseSelect} />
      {selectedCourse ? (
        <>
          <ScorecardOptions
            numHoles={numHoles}
            setNumHoles={setNumHoles}
            nineChoice={nineChoice}
            setNineChoice={setNineChoice}
            teeOptions={teeOptions}
            selectedTee={selectedTee}
            setSelectedTee={setSelectedTee}
          />
          <ScorecardTable
            scores={scores}
            handleScoreChange={handleScoreChange}
            holeData={holeData}
            selectedCourse={selectedCourse}
            teeOptions={teeOptions}
            selectedTee={selectedTee}
            totalScore={totalScore}
            numHoles={parseInt(numHoles, 10)}
            holeOffset={holeOffset}
          />
          <ScorecardFooter
            selectedCourse={selectedCourse}
            roundDate={roundDate}
            isCompetition={isCompetition}
            setIsCompetition={setIsCompetition}
            competitionDeadline={competitionDeadline}
            numHoles={parseInt(numHoles, 10)}
            onSubmit={finalSubmit}
            scorecardComplete={scorecardComplete}
            competitionWindowStart={competitionWindowStart}
          />
        </>
      ) : (
        <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
          Hey, try selecting a course to enter a new round!
        </Typography>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Scorecard;